<template>
  <div class="page approval">
    <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
    <SuccessAlert :message="successMessage" @clear-error="clearMessage" />

    <PageHeader title="Aprobare" :subtitle="subtitle" icon="mdi-stamper">
      <!-- the admins: every demer, or the ones they execute (types Configurare gives them) -->
      <div v-if="showAdminViews" class="view-switch" role="radiogroup" aria-label="Ce demersuri vedeți">
        <button
          type="button"
          role="radio"
          class="view-switch__chip"
          :class="{ 'is-active': adminView === 'mine' }"
          :aria-checked="adminView === 'mine'"
          @click="setAdminView('mine')"
        >
          <v-icon small>mdi-account-wrench-outline</v-icon>De executat de dvs.<span class="view-switch__count">{{ myWorkCount }}</span>
        </button>
        <button
          type="button"
          role="radio"
          class="view-switch__chip"
          :class="{ 'is-active': adminView === 'all' }"
          :aria-checked="adminView === 'all'"
          @click="setAdminView('all')"
        >
          Toate demersurile<span class="view-switch__count">{{ allCount }}</span>
        </button>
      </div>
      <DepartmentFilter
        v-model="department"
        :items="uniqueRows"
        field="department"
        :departments="approvalDepartments"
      />
    </PageHeader>

    <div class="page-body">
      <section class="panel">
        <div class="panel-toolbar">
          <h2 class="panel-title">
            {{ department || (isAdmin ? (adminView === "mine" ? "De preluat și în lucru" : "Toate demersurile") : (isEngineer || isEngineerPC) ? "De preluat și în lucru" : "Așteaptă decizia dvs.") }}
            <span class="count">{{ filteredData.length }}</span>
          </h2>
          <span class="grow-spacer" />
          <SearchBar @search="handleSearch" />
          <ColumnSelect :headers="headers" :updateVisibleColumns="updateVisibleColumns" pref-key="approval" />
        </div>
        <v-data-table
          :mobile-breakpoint="0"
          :headers="visibleHeaders"
          :items="paginatedItems"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :loading="isLoading"
          loading-text="Se încarcă demersurile..."
          :no-data-text="isAdmin ? (adminView === 'mine' ? 'Nimic de executat acum.' : 'Nu există demersuri') : 'Nimic de aprobat acum. Demersurile noi apar aici automat.'"
          :options.sync="pagination"
          :server-items-length="filteredData.length"
          @update:options="handleTableOptionsUpdate"
        >
          <template v-slot:item="{ item, index }">
            <tr
              class="approval-row"
              :class="{ 'light-green-row': index % 2 !== 0, 'row-rejected': item.status === 'Respins' }"
              @click="onViewClick(item)"
            >
              <td
                v-for="(header, hIndex) in visibleHeaders"
                :key="hIndex"
                :style="header.style ? header.style : {}"
              >
                <template v-if="header.value === 'comment'">
                  <div v-if="item.comment" class="comment-cell">
                    <div class="d-flex align-center">
                      <strong>{{ item.user }}</strong>
                      <span class="ml-2 comment-date">{{ formatDate(item.date) }}</span>
                    </div>
                    <div class="comment-text">{{ item.comment }}</div>
                  </div>
                  <div v-else class="no-comments">—</div>
                </template>
                <template v-else-if="header.value === 'comments'">
                  <div v-if="item.commentsHistory && item.commentsHistory.length" class="comments-wrapper">
                    <v-timeline dense class="compact-timeline">
                      <v-timeline-item
                        v-for="(comment, cIndex) in item.commentsHistory"
                        :key="cIndex"
                        small
                        :color="getStatusColor(comment.status)"
                        class="compact-item"
                      >
                        <div class="comment-header">
                          <strong class="user-name">{{ comment.user }}</strong>
                          <StatusChip :status="comment.status" small class="ml-2" />
                        </div>
                        <div v-if="comment.comment" class="comment-body">{{ comment.comment }}</div>
                        <PhotoGallery v-if="comment.files && comment.files.length" class="comment-photos" :files="comment.files" :size="44" download-name="demers" @click.native.stop />
                        <div class="comment-date">{{ formatDate(comment.date) }}</div>
                      </v-timeline-item>
                    </v-timeline>
                  </div>
                  <div v-else class="no-comments">—</div>
                </template>
                <StatusChip v-else-if="header.value === 'status'" :status="item.status" />
                <v-btn
                  v-else-if="header.value === 'actions'"
                  class="btn-green"
                  @click.stop="onViewClick(item)"
                >
                  <v-icon left>mdi-stamper</v-icon>Decide
                </v-btn>
                <template v-else>
                  {{ getTableCellValue(item, header, index) }}
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </section>
    </div>

    <ApprovalDetails
      ref="approvalDetails"
      :request="selectedRequest"
      :visible="showApprovalPopup"
      @updateRequestData="refreshApprovalData"
    />
  </div>
</template>

<script>
import activityLogsData from "./activityLogs.json";
import PageHeader from "@/components/layout/PageHeader.vue";
import StatusChip from "@/components/elements/StatusChip.vue";
import DepartmentFilter from "@/components/elements/DepartmentFilter.vue";
import ColumnSelect from "@/components/elements/ColumnSelect.vue";
import ApprovalDetails from "./ApprovalDetails.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import SearchBar from "@/components/elements/SearchBar.vue";
import PhotoGallery from "@/components/files/PhotoGallery.vue";
import * as api from "./api";
import { mapGetters } from "vuex";
import * as helper from "@/helper/helper.js";
import * as requestApi from "./api";
import { fetchApprovalQueue, fetchMyWork, uniqueByRequest } from "./queue";

export default {
  name: "RequestApproval",
  components: {
    PageHeader,
    StatusChip,
    DepartmentFilter,
    ApprovalDetails,
    ErrorAlert,
    ColumnSelect,
    SearchBar,
    SuccessAlert,
    PhotoGallery,
  },
  data() {
    return {
      headers: [],
      approvalData: [],
      // the admins: 'all' (every demer) or 'mine' (the ones they execute); chosen when the first list arrives
      adminView: null,
      allRows: [],
      myWork: [],
      department: "",
      visibleHeaders: [],
      selectedRequest: null,
      showApprovalPopup: false,
      activityLog: activityLogsData,
      search: "",
      sortBy: "id",
      errorMessage: "",
      successMessage: "",
      sortDesc: true,
      isLoading: true,
      showCommentsDialog: false,
      selectedComments: [],
      commentsCache: {},
      commentLoading: false,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["id"],
        sortDesc: [true]
      },
    };
  },
  methods: {
    async fetchApprovalData() {
      await this.refreshApprovalData();
    },

    async refreshApprovalData() {
      this.isLoading = true;
      // a decision or an item edit adds comments: don't show the ones cached before it
      this.commentsCache = {};
      try {
        const role = this.$store.getters.getUser.role;
        if (this.isAdmin) {
          // every demer, plus what the admins execute themselves (the badge counts only that)
          const [all, mine] = await Promise.all([fetchApprovalQueue(role), fetchMyWork(role)]);
          this.allRows = all;
          this.myWork = mine;
          if (!this.adminView) this.adminView = mine.length ? "mine" : "all";
          this.approvalData = this.adminView === "mine" ? mine : all;
          this.$store.commit("SET_APPROVAL_COUNT", uniqueByRequest(mine).length);
        } else {
          this.approvalData = await fetchApprovalQueue(role);
          this.$store.commit("SET_APPROVAL_COUNT", this.uniqueRows.length);
        }

        const requestIds = this.approvalData
          .filter(item => item.requestId)
          .map(item => item.requestId);

        if (requestIds.length > 0) {
          await this.fetchCommentsInBulk(requestIds);
        }
      } catch (error) {
        console.error("Error fetching approval data:", error);
        this.handleError("Lista nu a putut fi încărcată. Reîncărcați pagina.");
      } finally {
        this.isLoading = false;
      }
    },

    async fetchCommentsInBulk(requestIds) {
      if (this.commentLoading || requestIds.length === 0) return;
      
      try {
        this.commentLoading = true;
        
        const idsToFetch = requestIds.filter(id => !this.commentsCache[id]);
        
        if (idsToFetch.length === 0) {
          this.approvalData.forEach(item => {
            if (item.requestId && this.commentsCache[item.requestId]) {
              item.commentsHistory = this.commentsCache[item.requestId];
            }
          });
          this.commentLoading = false;
          return;
        }
        
        const commentsData = await requestApi.getBulkRequestCommentsHistory(idsToFetch);
        
        Object.entries(commentsData).forEach(([requestId, comments]) => {
          const numericId = parseInt(requestId, 10);
          this.commentsCache[numericId] = comments;
          
          const items = this.approvalData.filter(item => item.requestId === numericId);
          items.forEach(item => {
            item.commentsHistory = comments;
          });
        });
      } catch (error) {
        console.error("Error fetching bulk comments:", error);
      } finally {
        this.commentLoading = false;
      }
    },
    
    setAdminView(view) {
      if (this.adminView === view) return;
      this.adminView = view;
      this.approvalData = view === "mine" ? this.myWork : this.allRows;
      this.pagination = { ...this.pagination, page: 1 };
    },
    onViewClick(item) {
      this.selectedRequest = { ...item };
      this.$refs.approvalDetails.show();
    },
    formatCreatedAt(dateString) {
      const result = helper.formatCreatedAt(dateString);
      return result;
    },
    getTableCellValue(item, header, index) {
      if (header.value === "index") return (this.pagination.page - 1) * Math.max(this.pagination.itemsPerPage, 0) + index + 1;
      if (header.value === "user") return item.user;
      if (header.value === "status") return item.status;
      if (header.value === "equipment") return item.equipment;
      if (header.value === "cause") return item.cause;
      if (header.value === "department") return item.department;
      if (header.value === "model") return item.model;
      if (header.value === "date") return this.formatCreatedAt(item.date);
      return "";
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getStatusColor(status) {
      const statusColors = {
        'Respins': 'error',
        'Îndeplinit': 'success',
        'În lucru': 'warning',
        'Imposibil': 'grey',
        'Emis de utilizator': 'primary',
        'Aprobat de Sef al Departamentului': 'info',
        'Aprobat de Șef al Departamentului': 'info',
        'Aprobat de Rector': 'purple',
        'Aprobat de Contabilitate': 'deep-purple',
        'Corectat de utilizator': 'orange',
        'Anulat': 'red darken-3',
        'Date modificate': 'blue-grey',
        'Comentariu': 'deep-purple lighten-1'
      };
      return statusColors[status] || 'grey';
    },
    sortTable(header) {
      const result = helper.sortTable(header);
      return result;
    },
    updateVisibleColumns() {
      this.visibleHeaders = this.headers.filter((header) => header.visible);
    },
    handleSearch(searchTerm) {
      this.search = searchTerm;
    },
    handleError(message) {
      this.errorMessage = message;
    },
    handleSuccess(message) {
      this.successMessage = message;
    },
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    async showComments(item) {
      if (!item.requestId) return;
      try {
        this.selectedComments = await api.getRequestCommentsHistory(item.requestId);
        this.showCommentsDialog = true;
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    },
    handleTableOptionsUpdate(options) {
      console.log("Table options updated:", options);
      this.pagination = {
        page: options.page,
        itemsPerPage: options.itemsPerPage,
        sortBy: options.sortBy,
        sortDesc: options.sortDesc
      };
      
      this.sortBy = options.sortBy[0] || "id";
      this.sortDesc = options.sortDesc[0] || true;
      
      localStorage.setItem('approvalPaginationSettings', JSON.stringify({
        page: this.pagination.page,
        itemsPerPage: this.pagination.itemsPerPage,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc
      }));
      
      this.$nextTick(() => {
        if (this.paginatedItems.length > 0) {
          const requestIds = this.paginatedItems
            .filter(item => item.requestId)
            .map(item => item.requestId);
            
          if (requestIds.length > 0) {
            this.fetchCommentsInBulk(requestIds);
          }
        }
      });
    },
  },
  computed: {
    ...mapGetters(["getUser"]),
    isAdmin() {
      return this.$store.getters.getUser.role === "admin";
    },
    isEngineer() {
      return this.$store.getters.getUser.role === "inginer";
    },
    isEngineerPC() {
      return this.$store.getters.getUser.role === "inginer-pc";
    },
    isDtiSef() {
      return this.$store.getters.getUser.role === "dtisef";
    },
    isUserSef() {
      return this.$store.getters.getUser.role === "usersef";
    },
    isUser() {
      return this.$store.getters.getUser.role === "usersef";
    },
    isRector() {
      return this.$store.getters.getUser.role === "rector";
    },
    isContability() {
      return this.$store.getters.getUser.role === "contability";
    },
    // the departments they head + the sub-units without a Șef under them
    approvalDepartments() {
      return this.$store.getters.approvalDepartments;
    },
    // one row per request (a request can be in two queues of the same role)
    uniqueRows() {
      return uniqueByRequest(this.approvalData);
    },
    // only once Configurare gave the admins a type (something to execute), or while that list is on screen
    showAdminViews() {
      return this.isAdmin && (this.myWork.length > 0 || this.adminView === "mine");
    },
    myWorkCount() {
      return uniqueByRequest(this.myWork).length;
    },
    allCount() {
      return uniqueByRequest(this.allRows).length;
    },
    subtitle() {
      if (this.isAdmin && this.adminView === "mine") {
        return "Demersurile pe care le executați: preluați-le, apoi marcați-le îndeplinite. Apăsați pe un rând.";
      }
      if (this.isAdmin) return "Toate demersurile, în orice etapă. Apăsați pe un rând pentru detalii.";
      const headed = this.approvalDepartments.filter((d) => !d.inherited).length;
      if (this.isUserSef && headed > 1) {
        return `Demersurile celor ${headed} departamente pe care le conduceți. Apăsați pe un rând ca să decideți.`;
      }
      return "Demersurile care așteaptă decizia dvs. Apăsați pe un rând ca să decideți.";
    },
    filteredData() {
      const rows = this.department
        ? this.uniqueRows.filter((item) => item.department === this.department)
        : this.uniqueRows;
      return helper.filteredData(rows, this.search);
    },
    paginatedItems() {
      let sortedData = [...this.filteredData];
      
      if (this.pagination.sortBy && this.pagination.sortBy.length > 0) {
        const sortKey = this.pagination.sortBy[0];
        const sortDesc = this.pagination.sortDesc[0];
        
        sortedData.sort((a, b) => {
          let aValue, bValue;
          
          if (sortKey === 'date') {
            aValue = new Date(a.date || a.created_at).getTime();
            bValue = new Date(b.date || b.created_at).getTime();
          } else if (sortKey === 'id' || sortKey === 'requestId') {
            aValue = parseInt(a[sortKey]);
            bValue = parseInt(b[sortKey]);
          } else {
            aValue = a[sortKey];
            bValue = b[sortKey];
          }
          
          if (aValue < bValue) return sortDesc ? 1 : -1;
          if (aValue > bValue) return sortDesc ? -1 : 1;
          return 0;
        });
      }
      
      const startIndex = (this.pagination.page - 1) * this.pagination.itemsPerPage;
      const endIndex = startIndex + this.pagination.itemsPerPage;
      return sortedData.slice(startIndex, endIndex);
    }
  },
  mounted() {
    this.headers = require("./approvalData.json");
    this.visibleHeaders = this.headers.filter(
      (header) => header.defaultVisible
    );
    const many = this.approvalDepartments.length > 1;
    this.headers.forEach((header) => {
      header.visible = header.defaultVisible || (many && header.value === "department");
    });
    this.visibleHeaders = this.headers.filter((header) => header.visible);
    
    this.$refs.approvalDetails.$on("error", this.handleError);
    this.$refs.approvalDetails.$on("success", this.handleSuccess);
    // a notification brought something new for this person: show it without F5
    this.$root.$on("approval-queue-changed", this.refreshApprovalData);
    
    this.$nextTick(() => {
      this.fetchApprovalData();
    });

    const savedPaginationSettings = localStorage.getItem('approvalPaginationSettings');
    if (savedPaginationSettings) {
      try {
        const settings = JSON.parse(savedPaginationSettings);
        this.pagination = {
          page: settings.page || 1,
          itemsPerPage: settings.itemsPerPage || 10,
          sortBy: settings.sortBy || ["id"],
          sortDesc: settings.sortDesc || [true]
        };
        this.sortBy = this.pagination.sortBy[0] || "id";
        this.sortDesc = this.pagination.sortDesc[0] !== false;
      } catch (e) {
        console.error('Error parsing saved pagination settings:', e);
      }
    } else {
      this.pagination.sortBy = ["id"];
      this.pagination.sortDesc = [true];
      this.sortBy = "id";
      this.sortDesc = true;
    }
  },
  watch: {
    filteredData: {
      handler(newVal) {
        if (!this.isLoading && newVal.length > 0) {
          const requestIds = newVal
            .filter(item => item.requestId)
            .map(item => item.requestId);
          
          if (requestIds.length > 0) {
            this.$nextTick(() => {
              this.fetchCommentsInBulk(requestIds);
            });
          }
        }
      },
      deep: true
    },
    pagination: {
      handler(newVal, oldVal) {
        if (newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage) {
          if (this.paginatedItems.length > 0) {
            const requestIds = this.paginatedItems
              .filter(item => item.requestId)
              .map(item => item.requestId);
              
            if (requestIds.length > 0) {
              this.fetchCommentsInBulk(requestIds);
            }
          }
        }
      },
      deep: true
    },
  },
  beforeDestroy() {
    this.$root.$off("approval-queue-changed", this.refreshApprovalData);
    localStorage.setItem('approvalPaginationSettings', JSON.stringify({
      page: this.pagination.page,
      itemsPerPage: this.pagination.itemsPerPage,
      sortBy: this.pagination.sortBy,
      sortDesc: this.pagination.sortDesc
    }));
  }
};
</script>

<style scoped>
.approval-row {
  cursor: pointer;
}
/* the admins' two lists, on the dark band (same look as DepartmentFilter) */
.view-switch {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.view-switch__chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 6px 8px 6px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  font: inherit;
  font-size: 0.98rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}
.view-switch__chip:hover {
  background: rgba(255, 255, 255, 0.12);
}
.view-switch__chip .v-icon {
  color: inherit !important;
}
.view-switch__chip.is-active {
  background: var(--leaf);
  border-color: var(--leaf);
  color: var(--pine-900);
}
.view-switch__count {
  min-width: 28px;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  font-size: 0.85rem;
  text-align: center;
}
.view-switch__chip.is-active .view-switch__count {
  background: rgba(14, 42, 33, 0.14);
}
.comment-cell {
  max-width: 320px;
  padding: 8px 10px;
  background: var(--mint-50);
  border-radius: 8px;
}
.comment-text {
  color: var(--ink-2);
  margin-top: 2px;
  white-space: normal;
  word-break: break-word;
}
.comments-wrapper {
  max-height: 160px;
  overflow-y: auto;
  padding: 4px 0;
}
.compact-timeline {
  padding-top: 0;
}
.compact-item {
  padding-bottom: 8px !important;
}
.comment-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 0;
  font-size: 0.92rem;
}
.user-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}
.comment-body {
  font-size: 0.92rem;
  color: var(--ink-2);
  margin: 2px 0;
  white-space: normal;
  word-break: break-word;
}
.comment-photos {
  margin: 4px 0 2px;
}
.comment-date {
  font-size: 0.8rem;
  color: var(--ink-3);
}
.no-comments {
  color: var(--ink-3);
}
</style>
